import { Controller } from "@hotwired/stimulus"
import { loadPdf } from "../helpers/load_pdf"

export default class extends Controller {
  static targets = ["loadingState", "pagesContainer", "saveStatus"]

  static values = {
    pdfUrl: String,
    saveUrl: String,
    finalizeUrl: String,
    downloadUrl: String,
    schema: String,
    fieldData: String,
    pageCount: Number,
    i18nLoading: String,
    i18nSaving: String,
    i18nSaved: String,
    i18nError: String,
    i18nFinalized: String,
    i18nFinalizeError: String,
    i18nPage: String,
  }

  connect() {
    this.schema = this.parseJsonValue(this.schemaValue, [])
    this.fieldData = this.parseJsonValue(this.fieldDataValue, {})
    this.pageWrappers = new Map()
    this.pageViewports = new Map()
    this.inputIndex = new Map()
    this.saveTimeout = null
    this.saveInFlight = null
    this.renderPdf()
  }

  disconnect() {
    clearTimeout(this.saveTimeout)
  }

  async renderPdf() {
    this.setStatus(this.i18nLoadingValue)

    try {
      const pdf = await loadPdf(this.pdfUrlValue)
      const containerWidth = this.pagesContainerTarget.clientWidth || 900

      this.pagesContainerTarget.innerHTML = ""
      this.pageWrappers.clear()
      this.pageViewports.clear()

      for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
        const page = await pdf.getPage(pageNumber)
        const baseViewport = page.getViewport({ scale: 1 })
        const scale = Math.min((containerWidth - 32) / baseViewport.width, 1.6)
        const viewport = page.getViewport({ scale })

        this.pageViewports.set(pageNumber, {
          width: baseViewport.width,
          height: baseViewport.height,
          scale,
        })

        const wrapper = this.buildPageWrapper(pageNumber, viewport)
        const canvas = wrapper.querySelector("canvas")

        await page.render({
          canvasContext: canvas.getContext("2d"),
          viewport,
        }).promise

        this.pageWrappers.set(pageNumber, wrapper)
        this.pagesContainerTarget.appendChild(wrapper)
      }

      this.renderFields()
      this.loadingStateTarget.remove()
      this.setStatus("")
    } catch (error) {
      console.error("PDF form render failed", error)
      this.setStatus(this.i18nErrorValue, true)
    }
  }

  buildPageWrapper(pageNumber, viewport) {
    const wrapper = document.createElement("section")
    wrapper.className = "overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm"
    wrapper.dataset.page = String(pageNumber)

    const header = document.createElement("div")
    header.className =
      "border-b border-gray-100 px-4 py-3 text-xs font-medium uppercase tracking-wide text-gray-500"
    header.textContent = `${this.i18nPageValue} ${pageNumber}`

    const body = document.createElement("div")
    body.className = "relative overflow-auto bg-gray-100 p-4"

    const pageFrame = document.createElement("div")
    pageFrame.className = "relative mx-auto shadow-sm"
    pageFrame.style.width = `${viewport.width}px`
    pageFrame.style.height = `${viewport.height}px`

    const canvas = document.createElement("canvas")
    canvas.width = viewport.width
    canvas.height = viewport.height
    canvas.className = "block bg-white"

    const overlay = document.createElement("div")
    overlay.className = "absolute inset-0"
    overlay.dataset.pageOverlay = String(pageNumber)

    pageFrame.appendChild(canvas)
    pageFrame.appendChild(overlay)
    body.appendChild(pageFrame)
    wrapper.appendChild(header)
    wrapper.appendChild(body)

    return wrapper
  }

  renderFields() {
    this.inputIndex.clear()

    this.schema.forEach((field) => {
      const widgets = Array.isArray(field.widgets) ? field.widgets : []
      const value = this.valueForField(field)

      widgets.forEach((widget, widgetIndex) => {
        const wrapper = this.pageWrappers.get(widget.page)
        const viewport = this.pageViewports.get(widget.page)
        if (!wrapper || !viewport) return

        const overlay = wrapper.querySelector(`[data-page-overlay="${widget.page}"]`)
        if (!overlay) return

        const input = this.buildFieldInput(field, widget, widgetIndex, value)
        if (!input) return

        this.positionInput(input, widget.rect, viewport)
        overlay.appendChild(input)
        this.indexInput(field.name, input)
      })
    })
  }

  buildFieldInput(field, widget, widgetIndex, value) {
    const inputClasses =
      "absolute rounded border border-green-300 bg-white/95 px-2 py-1 text-sm text-gray-900 shadow-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-200"

    let input

    switch (field.field_type) {
      case "text":
        input = document.createElement("input")
        input.type = "text"
        input.value = typeof value === "string" ? value : ""
        break
      case "textarea":
        input = document.createElement("textarea")
        input.value = typeof value === "string" ? value : ""
        input.className = `${inputClasses} resize-none`
        break
      case "checkbox":
        input = document.createElement("input")
        input.type = "checkbox"
        input.checked = value === true
        input.className =
          "absolute h-4 w-4 rounded border-green-400 text-green-600 focus:ring-green-300"
        break
      case "radio":
        input = document.createElement("input")
        input.type = "radio"
        input.value = widget.option_value || ""
        input.checked = value === input.value
        input.className = "absolute h-4 w-4 border-green-400 text-green-600 focus:ring-green-300"
        break
      case "select":
        input = document.createElement("select")
        input.className = `${inputClasses} pr-7`
        if (!field.multiple) {
          const blankOption = document.createElement("option")
          blankOption.value = ""
          blankOption.textContent = ""
          input.appendChild(blankOption)
        }
        ;(field.options || []).forEach((option) => {
          const optionElement = document.createElement("option")
          optionElement.value = option.value
          optionElement.textContent = option.label
          optionElement.selected = Array.isArray(value)
            ? value.includes(option.value)
            : value === option.value
          input.appendChild(optionElement)
        })
        if (field.multiple) input.multiple = true
        break
      default:
        return null
    }

    if (!input.className) input.className = inputClasses
    if (field.read_only) input.disabled = true

    input.dataset.fieldName = field.name
    input.dataset.fieldType = field.field_type
    input.dataset.widgetIndex = String(widgetIndex)
    input.title = field.label || field.name

    input.addEventListener("input", (event) => this.handleInput(event))
    input.addEventListener("change", (event) => this.handleInput(event))

    return input
  }

  positionInput(input, rect, viewport) {
    const width = Math.max(rect.width * viewport.scale, 18)
    const height = Math.max(rect.height * viewport.scale, 18)
    const left = rect.x * viewport.scale
    const top = (viewport.height - (rect.y + rect.height)) * viewport.scale

    input.style.left = `${left}px`
    input.style.top = `${top}px`
    input.style.width = `${width}px`
    input.style.height = `${height}px`
  }

  handleInput(event) {
    const input = event.currentTarget
    const fieldName = input.dataset.fieldName
    const fieldType = input.dataset.fieldType

    this.fieldData[fieldName] = this.readInputValue(input, fieldType)
    this.syncLinkedInputs(fieldName, input, fieldType)
    this.scheduleSave()
  }

  readInputValue(input, fieldType) {
    switch (fieldType) {
      case "checkbox":
        return input.checked
      case "radio":
        return input.checked ? input.value : this.fieldData[input.dataset.fieldName] || ""
      case "select":
        if (input.multiple) {
          return Array.from(input.selectedOptions).map((option) => option.value)
        }
        return input.value
      default:
        return input.value
    }
  }

  syncLinkedInputs(fieldName, changedInput, fieldType) {
    const inputs = this.inputIndex.get(fieldName) || []

    inputs.forEach((input) => {
      if (input === changedInput) return

      if (fieldType === "checkbox") {
        input.checked = changedInput.checked
      } else if (fieldType === "radio") {
        input.checked = input.value === this.fieldData[fieldName]
      } else if (fieldType === "select" && input.multiple) {
        const values = this.fieldData[fieldName] || []
        Array.from(input.options).forEach((option) => {
          option.selected = values.includes(option.value)
        })
      } else {
        input.value = changedInput.value
      }
    })
  }

  indexInput(fieldName, input) {
    if (!this.inputIndex.has(fieldName)) this.inputIndex.set(fieldName, [])
    this.inputIndex.get(fieldName).push(input)
  }

  valueForField(field) {
    if (Object.prototype.hasOwnProperty.call(this.fieldData, field.name)) {
      return this.fieldData[field.name]
    }
    return field.value
  }

  scheduleSave() {
    clearTimeout(this.saveTimeout)
    this.setStatus(this.i18nSavingValue)
    this.saveTimeout = setTimeout(() => this.save(), 500)
  }

  async saveNow() {
    await this.save()
  }

  async save() {
    if (this.saveInFlight) return this.saveInFlight

    this.saveInFlight = fetch(this.saveUrlValue, {
      method: "PATCH",
      headers: this.jsonHeaders(),
      body: JSON.stringify({
        pdf_form_filling: {
          field_data: this.fieldData,
        },
      }),
    })
      .then(async (response) => {
        const payload = await response.json()
        if (!response.ok || payload.status !== "ok")
          throw new Error(payload.error || this.i18nErrorValue)
        this.setStatus(this.i18nSavedValue)
      })
      .catch((error) => {
        console.error("PDF form save failed", error)
        this.setStatus(this.i18nErrorValue, true)
        throw error
      })
      .finally(() => {
        this.saveInFlight = null
      })

    return this.saveInFlight
  }

  async finalize() {
    try {
      await this.save()
      this.setStatus(this.i18nSavingValue)

      const response = await fetch(this.finalizeUrlValue, {
        method: "POST",
        headers: this.jsonHeaders(),
        body: JSON.stringify({
          pdf_form_filling: {
            field_data: this.fieldData,
          },
        }),
      })

      const payload = await response.json()
      if (!response.ok || payload.status !== "ok") {
        throw new Error(payload.error || this.i18nFinalizeErrorValue)
      }

      this.setStatus(this.i18nFinalizedValue)
      if (payload.redirect_url) {
        window.location.href = payload.redirect_url
      }
    } catch (error) {
      console.error("PDF form finalize failed", error)
      this.setStatus(this.i18nFinalizeErrorValue, true)
    }
  }

  async download() {
    try {
      await this.save()
      window.location.href = this.downloadUrlValue
    } catch (error) {
      console.error("PDF form download failed", error)
      this.setStatus(this.i18nErrorValue, true)
    }
  }

  parseJsonValue(value, fallback) {
    try {
      return value ? JSON.parse(value) : fallback
    } catch (_error) {
      return fallback
    }
  }

  jsonHeaders() {
    return {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
    }
  }

  setStatus(message, isError = false) {
    if (!this.hasSaveStatusTarget) return

    this.saveStatusTarget.textContent = message
    this.saveStatusTarget.classList.remove("text-gray-500", "text-red-600", "text-green-600")
    if (!message) return

    if (isError) {
      this.saveStatusTarget.classList.add("text-red-600")
    } else {
      this.saveStatusTarget.classList.add("text-green-600")
    }
  }
}
